import type { PaginatedResponse } from '@kizen/api';
import type {
  Activity,
  ActivityDetail,
  ActivityField,
  ActivityHistory,
  ActivityHistoryParams,
  ActivityUsage,
  ScheduledActivity,
  SearchScheduledActivitiesBody,
  SearchScheduledActivitiesParams,
} from '@kizen/api/activities';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import { type AxiosInstance } from 'axios';
import type { Resource } from './resource';
import type { Drop, WithParams, WithRequired } from './types';

import {
  getActivity,
  getActivityFields,
  getActivityHistory,
  getActivityUsage,
  searchScheduledActivities,
} from '@kizen/api/activities';
import { getResource } from './resource';
import { curry1 } from './utils';

type ActivityQuery<TData = ActivityDetail> = WithRequired<
  QueryObserverOptions<
    ActivityDetail,
    DefaultError,
    TData,
    ActivityDetail,
    [Resource<Readonly<['activities', string]>>]
  >,
  'queryFn' | 'queryKey'
>;

type ActivityFieldsQuery<TData = ActivityField[]> = WithRequired<
  QueryObserverOptions<
    ActivityField[],
    DefaultError,
    TData,
    ActivityField[],
    [Resource<Readonly<['activities', string, 'fields']>>]
  >,
  'queryFn' | 'queryKey'
>;

type ActivityHistoryQuery<TData = ActivityHistory> = WithRequired<
  QueryObserverOptions<
    ActivityHistory,
    DefaultError,
    TData,
    ActivityHistory,
    [
      Resource<
        Readonly<['activities', string, 'history']>,
        ActivityHistoryParams
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

type SearchScheduledActivitiesQuery<
  TData = PaginatedResponse<ScheduledActivity>,
> = WithRequired<
  QueryObserverOptions<
    PaginatedResponse<ScheduledActivity>,
    DefaultError,
    TData,
    PaginatedResponse<ScheduledActivity>,
    [
      Resource<
        Readonly<['activities', 'scheduled-activity', 'search']>,
        SearchScheduledActivitiesParams,
        SearchScheduledActivitiesBody
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

type SearchScheduledActivitiesQueryOptions<
  TData = PaginatedResponse<ScheduledActivity>,
> = WithParams<
  Omit<SearchScheduledActivitiesQuery<TData>, 'queryFn' | 'queryKey'>,
  SearchScheduledActivitiesParams
> & {
  body?: SearchScheduledActivitiesBody;
};

type GetActivityUsageQuery<TData = ActivityUsage> = WithRequired<
  QueryObserverOptions<
    ActivityUsage,
    DefaultError,
    TData,
    ActivityUsage,
    [Resource<Readonly<['activities', string, 'usage']>>]
  >,
  'queryFn' | 'queryKey'
>;

export const activityQuery = <TData = Activity>(
  axios: AxiosInstance,
  id: string,
  options?: Omit<ActivityQuery<TData>, 'queryKey' | 'queryFn'>
): ActivityQuery<TData> => {
  const path = ['activities', id] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getActivity(axios, qk.path[1], { signal });
    },
    ...options,
  };
};

export const activityFieldsQuery = <TData = ActivityField[]>(
  axios: AxiosInstance,
  id: string,
  options?: Omit<ActivityFieldsQuery<TData>, 'queryKey' | 'queryFn'>
): ActivityFieldsQuery<TData> => {
  const path = ['activities', id, 'fields'] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getActivityFields(axios, qk.path[1], { signal });
    },
    ...options,
  };
};

export const activityHistoryQuery = <TData = ActivityHistory>(
  axios: AxiosInstance,
  id: string,
  options: WithParams<
    Omit<ActivityHistoryQuery<TData>, 'queryFn' | 'queryKey'>,
    ActivityHistoryParams
  > = {}
): ActivityHistoryQuery<TData> => {
  const path = ['activities', id, 'history'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getActivityHistory(axios, qk.path[1], {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

export const searchScheduledActivitiesQuery = <
  TData = PaginatedResponse<ScheduledActivity>,
>(
  axios: AxiosInstance,
  options: SearchScheduledActivitiesQueryOptions<TData> = {}
): SearchScheduledActivitiesQuery<TData> => {
  const path = ['activities', 'scheduled-activity', 'search'] as const;
  const { body, params, ...queryOpts } = options;
  const resource = getResource(path, { method: 'POST', body, params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return searchScheduledActivities(axios, {
        body: qk.body,
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

const getActivityUsageQuery = <TData = ActivityUsage>(
  axios: AxiosInstance,
  activityId: string,
  options?: Omit<
    QueryObserverOptions<
      ActivityUsage,
      DefaultError,
      TData,
      ActivityUsage,
      [Resource<Readonly<['activities', string, 'usage']>>]
    >,
    'queryKey' | 'queryFn'
  >
): GetActivityUsageQuery<TData> => {
  const path = ['activities', activityId, 'usage'] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getActivityUsage(axios, qk.path[1], { signal });
    },
    ...options,
  };
};

class Queries {
  public activityQuery: <TData = ActivityDetail>(
    ...args: Drop<Parameters<typeof activityQuery<TData>>>
  ) => ActivityQuery<TData>;
  public activityFieldsQuery: <TData = ActivityField[]>(
    ...args: Drop<Parameters<typeof activityFieldsQuery<TData>>>
  ) => ActivityFieldsQuery<TData>;
  public activityHistoryQuery: <TData = ActivityHistory>(
    ...args: Drop<Parameters<typeof activityHistoryQuery<TData>>>
  ) => ActivityHistoryQuery<TData>;
  public searchScheduledActivitiesQuery: <
    TData = PaginatedResponse<ScheduledActivity>,
  >(
    ...args: Drop<Parameters<typeof searchScheduledActivitiesQuery<TData>>>
  ) => SearchScheduledActivitiesQuery<TData>;
  public getActivityUsageQuery: <TData = ActivityUsage>(
    ...args: Drop<Parameters<typeof getActivityUsageQuery<TData>>>
  ) => GetActivityUsageQuery<TData>;

  constructor(axios: AxiosInstance) {
    this.activityQuery = curry1(activityQuery)(axios);
    this.activityFieldsQuery = curry1(activityFieldsQuery)(axios);
    this.activityHistoryQuery = curry1(activityHistoryQuery)(axios);
    this.searchScheduledActivitiesQuery = curry1(
      searchScheduledActivitiesQuery
    )(axios);
    this.getActivityUsageQuery = curry1(getActivityUsageQuery)(axios);
  }
}

export type ActivityQueries = Queries;

export const defActivityQueries = (axios: AxiosInstance): ActivityQueries => {
  return new Queries(axios);
};
